import { useAppServices } from 'hook/services'
import { useEffect, useRef, useState } from 'react'

const formatDate = (d) => {
  if (!d) return '—'
  const dt = new Date(d)
  if (isNaN(dt)) return '—'
  return dt.toLocaleDateString('en-AU')
}

const centsToAud = (cents) => {
  const n = Number(cents || 0)
  return (n / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })
}

export default function useAppData() {
  const AppService = useAppServices()
  const [list, setList] = useState([])
  const [loader, setLoader] = useState(true)
  const [downloading, setDownloading] = useState(false)

  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null })
  const [listingSlug, setListingSlug] = useState('')
  const [email, setEmail] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 0,
    totalItems: 0,
  })

  const slugTimeoutRef = useRef(null)
  const emailTimeoutRef = useRef(null)

  const buildQuery = (overrides = {}) => {
    const params = {
      page: overrides.page ?? pagination.page,
      limit: overrides.limit ?? pagination.limit,
      startDate: dateRange.startDate ? dateRange.startDate.toISOString() : 'null',
      endDate: dateRange.endDate ? dateRange.endDate.toISOString() : 'null',
      listingSlug: (overrides.listingSlug ?? listingSlug)?.trim() || 'null',
      email: (overrides.email ?? email)?.trim() || 'null',
      status: statusFilter || 'null',
      ...(overrides.all ? { all: '1' } : {}),
    }
    return Object.entries(params)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&')
  }

  const fetchList = async (page = 1, limit = pagination.limit) => {
    setLoader(true)
    try {
      const { response } = await AppService.boost.adminList({
        query: buildQuery({ page, limit }),
        toaster: false,
      })
      if (response?.success) {
        setList(response.data || [])
        setPagination({
          page: response.pagination?.page || page,
          limit: response.pagination?.limit || limit,
          totalPages: response.pagination?.totalPages || 0,
          totalItems: response.pagination?.totalItems || 0,
        })
      } else {
        setList([])
      }
    } catch (err) {
      console.error('Boost admin list error:', err)
      setList([])
    } finally {
      setLoader(false)
    }
  }

  useEffect(() => {
    fetchList(1, pagination.limit)
  }, [dateRange, statusFilter])

  useEffect(() => {
    if (slugTimeoutRef.current) clearTimeout(slugTimeoutRef.current)
    slugTimeoutRef.current = setTimeout(() => fetchList(1, pagination.limit), 700)
    return () => clearTimeout(slugTimeoutRef.current)
  }, [listingSlug])

  useEffect(() => {
    if (emailTimeoutRef.current) clearTimeout(emailTimeoutRef.current)
    emailTimeoutRef.current = setTimeout(() => fetchList(1, pagination.limit), 700)
    return () => clearTimeout(emailTimeoutRef.current)
  }, [email])

  const handlePageChange = (newPage) => fetchList(newPage, pagination.limit)
  const handleLimitChange = (newLimit) => fetchList(1, newLimit)

  const downloadCsv = async () => {
    setDownloading(true)
    try {
      const { response } = await AppService.boost.adminList({
        query: buildQuery({ all: true }),
        toaster: false,
      })
      const rows = response?.data || []

      const header = [
        'Start Date',
        'End Date',
        'Listing Slug',
        'Listing Title',
        'Host Email',
        'Host Name',
        'Status',
        'Price (AUD)',
        'Location',
        'State',
        'Week Key',
        'Group ID',
      ]

      const escape = (val) => {
        const s = val == null ? '' : String(val)
        if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
        return s
      }

      const lines = [header.map(escape).join(',')]
      rows.forEach((b) => {
        lines.push(
          [
            formatDate(b.weekStart),
            formatDate(b.weekEnd),
            b.listing?.slug || '',
            b.listing?.title || '',
            b.host?.email || '',
            b.host?.display_name ||
              `${b.host?.first_name || ''} ${b.host?.last_name || ''}`.trim(),
            b.status || '',
            centsToAud(b.pricePaid),
            b.location?.label || b.location?.value || '',
            b.state || '',
            b.weekKey || '',
            b.groupId || '',
          ].map(escape).join(',')
        )
      })

      const csv = '﻿' + lines.join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `boost-listings-${new Date().toISOString().slice(0, 10)}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Boost CSV export error:', err)
    } finally {
      setDownloading(false)
    }
  }

  return {
    loader,
    downloading,
    dateRange,
    setDateRange,
    listingSlug,
    setListingSlug,
    email,
    setEmail,
    statusFilter,
    setStatusFilter,
    pagination,
    handlePageChange,
    handleLimitChange,
    downloadCsv,
    dataTableData: {
      columns: [
        { Header: 'Start Date', accessor: 'startDate' },
        { Header: 'End Date', accessor: 'endDate' },
        { Header: 'Listing Slug', accessor: 'slug' },
        { Header: 'Listing Title', accessor: 'title' },
        { Header: 'Host Email', accessor: 'email' },
        { Header: 'Status', accessor: 'status' },
        { Header: 'Price', accessor: 'price' },
        { Header: 'Location', accessor: 'location' },
        { Header: 'State', accessor: 'state' },
      ],
      rows: list.map((b) => ({
        startDate: formatDate(b.weekStart),
        endDate: formatDate(b.weekEnd),
        slug: b.listing?.slug || '—',
        title: b.listing?.title || '—',
        email: b.host?.email || '—',
        status: b.status || '—',
        price: centsToAud(b.pricePaid),
        location: b.location?.label || b.location?.value || '—',
        state: b.state || '—',
      })),
    },
  }
}
